import React, { useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf"; // ✅ Import jsPDF
import "jspdf-autotable"; // ✅ Import autoTable for table generation
import OrderListTwo from "./OrderListTwo";
import { CSVLink } from "react-csv";
import "./HandoverPage.css";

const HandoverPage = () => {
  const [couriers, setCouriers] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [statusFilter, setStatusFilter] = useState("To Pack");
  const [refreshSignal, setRefreshSignal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [totalToPackOrders, setTotalToPackOrders] = useState(0);

  // NEW: State for the selected date filter (defaults to today, YYYY-MM-DD)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  // Fetch couriers on component mount (and when refreshSignal changes)
  useEffect(() => {
    axios.get("https://astrovidhya.in/api/couriers/to-pack")
      .then((response) => {
        setCouriers(response.data);
        setTotalToPackOrders(
          response.data.reduce((sum, courier) => sum + (courier.totalToPack || 0), 0)
        );
      })
      .catch((error) => console.error("Error fetching couriers:", error));
  }, [refreshSignal]);

  // Handle Total row click - show all orders of active couriers
  const handleCourierClick = (courierId) => {
    console.log("Clicked Courier ID:", courierId); // ✅ Debugging log
    setSelectedCourier(courierId);
    setRefreshSignal((prev) => !prev); // ✅ Refresh the order list
    setSelectedOrders([]); // ✅ Clear selections
  };
  
  const handleTotalClick = () => {
    console.log("Clicked Total Row - Resetting Filter");
    setSelectedCourier(null); // ✅ Reset courier filter
    setRefreshSignal((prev) => !prev); // ✅ Refresh order list
    setSelectedOrders([]);
  };
  
  

  // Handle date change from the date picker
  const handleDateChange = (event) => {
    const selected = event.target.value;
    console.log("📅 Selected Date Changed:", selected);
    setSelectedDate(selected);
  };
  

  // Handle Select All
  const selectAllOrders = () => {
    if (!orders || orders.length === 0) {
      alert("No orders available to select!");
      return;
    }
    setSelectedOrders(orders.map((order) => order._id));
  };

  // Handle Clear All
  const clearAllOrders = () => {
    setSelectedOrders([]);
  };

  // Handle Submit (Mark as Done)
  const markAsDone = async () => {
    if (selectedOrders.length === 0) {
      alert("No orders selected!");
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        "https://astrovidhya.in/api/orders/update-status",
        {
          orderIds: selectedOrders,
          status: "done",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSelectedOrders([]);
      await fetchOrders(); // refresh orders after updating
      alert("Selected orders marked as done!");
    } catch (error) {
      console.error("Error updating order status:", error);
      alert("Failed to update order status. Please try again.");
    }
  };

  // Function to fetch orders (used by OrderListTwo via props, if needed)
  const fetchOrders = async () => {
    const token = localStorage.getItem("authToken");
    try {
      const response = await axios.get("https://astrovidhya.in/api/orders", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
      alert("Failed to fetch orders. Please try again.");
    }
  };

  // Generate Manifest PDF (for dispatch)
  const generateManifestPDF = () => {
    const pdf = new jsPDF("portrait");

    // Get selected courier name (if any)
    const courierName = couriers.find(c => c._id === selectedCourier)?.name || "N/A";

    // Header Section
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text(`Manifest for: ${courierName}`, 10, 20);
    pdf.text(`Date: ${new Date().toLocaleDateString()}`, 10, 30);

    // Right-Side Address Block
    pdf.setFontSize(12);
    const addressX = pdf.internal.pageSize.width - 100;
    pdf.text("PRAGNAY INFO", addressX, 20);
    pdf.text("102 Simrol Road, near Hotel Amber", addressX, 30);
    pdf.text("Mobile No : 7400668735", addressX, 40);
    pdf.text("MHOW DIST INDORE MP 453441 Pincode", addressX, 50);

    // Orders Table (Only Selected Orders)
    const tableHeaders = ["S.No", "Customer Name", "Product", "AWD No.", "Order No."];
    const tableData = selectedOrders.map((orderId, index) => {
      const order = orders.find(o => o._id === orderId);
      return [
        index + 1,
        order?.customer_name || "N/A",
        order?.products?.[0]?.productName || "N/A",
        order?.AWD || "N/A",
        order?._id.toUpperCase() || "N/A"
      ];
    });

    pdf.autoTable({
      head: [tableHeaders],
      body: tableData,
      startY: 60
    });

    pdf.save(`Manifest_${courierName}.pdf`);
  };

  // Generate CSV Data for download (if needed)
  const generateCSVData = () => {
    const data = selectedOrders.length > 0
      ? orders
          .filter((order) => selectedOrders.includes(order._id))
          .map((order) => ({
            "Order ID": order._id.toUpperCase(),
            "Customer Name": order.customer_name || "N/A",
            "Mobile": order.mobile || "N/A",
            "Alternate Mobile": order.customer_mobile_2 || "N/A",
            "Shipping Address": order.shipping_address || "N/A",
            "City": order.city || "N/A",
            "State": order.state || "N/A",
            "District": order.district || "N/A",
            "AWD No.": order.AWD || "N/A",
            "Courier": order.courier?.name || "N/A",
            "Order Date": order.order_date
              ? new Date(order.order_date).toLocaleDateString()
              : "N/A",
            "Approval Status": order.approval_status || "Pending",
            "Order Time": order.order_time || "N/A",
            "Caller": order.caller || "N/A",
            "Products": order.products
              ? order.products.map((p) => p.productName).join(", ")
              : "N/A",
            "Total Amount (₹)": order.total_amount || "0",
          }))
      : [];
    setCsvData(data);
  };

  return (
    <div className="handover-page p-4">
      <h2 className="text-lg font-bold mb-4">Handover Page</h2>

      {/* Courier Panel */}
      <table className="table-auto w-full border mb-4">
  <thead>
    <tr>
      <th className="border px-2 py-1">Courier Name</th>
      <th className="border px-2 py-1">Total To Pack Orders</th>
    </tr>
  </thead>
  <tbody>
    {couriers.map((courier) => (
      <tr 
        key={courier._id}
        onClick={() => handleCourierClick(courier._id)} // ✅ Attach the function
        className={`cursor-pointer hover:bg-gray-100 ${
          selectedCourier === courier._id ? "bg-blue-200" : ""
        }`}
      >
        <td className="border px-2 py-1">{courier.name}</td>
        <td className="border px-2 py-1">{courier.totalToPack}</td>
      </tr>
    ))}

    {/* ✅ Add "Total" Row to Reset Filter */}
    <tr
      onClick={handleTotalClick} // ✅ Attach reset function
      className="cursor-pointer bg-gray-300 font-bold hover:bg-gray-400"
    >
      <td className="border px-2 py-1">Total</td>
      <td className="border px-2 py-1">{totalToPackOrders}</td>
    </tr>
  </tbody>
</table>


      {/* Date Picker Element (aligned right) */}
      <div className="flex justify-end mb-4">
        <label className="py-3 mr-2 font-bold text-gray-700">Select Date:</label>
        <input
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          className="px-3 py-2 border rounded-md"
        />
      </div>

      {/* Order List */}
      <OrderListTwo
        refreshSignal={refreshSignal} // Trigger refresh when filters change
        statusFilter={statusFilter} // Filter by status
        courierFilter={selectedCourier} // ✅ Add the courier filter
      
        dateFilter={selectedDate} // Pass the selected date as a filter
        setSelectedOrders={setSelectedOrders} // For updating selected orders
        selectedOrders={selectedOrders}
        setOrders={setOrders} // To update parent orders state
        couriers={couriers} // Pass couriers to OrderListTwo if needed
        pageType="handover" // Identify this as the Handover Page
      />

      {/* Action Buttons */}
      <div className="mt-4 flex space-x-4">
        <button
          onClick={markAsDone}
          className="ml-4 mt-4 bg-[#004C59] text-white px-4 py-2 rounded w-1/6"
        >
          Mark as Done
        </button>
      </div>

      {/* CSV Download Button (if needed) 
      <div className="mt-4">
        <CSVLink
          data={csvData}
          filename={`Manifest_${new Date().toISOString().split("T")[0]}.csv`}
          className="bg-green-500 text-white px-4 py-2 rounded"
          onClick={generateCSVData}
        >
          Download Manifest CSV
        </CSVLink>
      </div>

      {/* PDF Buttons 
      <div className="mt-4 flex space-x-4">
        <button
          onClick={generateManifestPDF}
          className="generate-manifest-btn bg-blue-600 text-white px-4 py-2 rounded w-1/6"
        >
          Download Manifest & Initiate Dispatch
        </button>
      </div>*/}
    </div>
  );
};

export default HandoverPage;
